import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import { useIsFetching, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AppLayout,
  EmptyState,
  Mtds,
  PencilSimpleIcon,
  PlusIcon,
  ScreenHeader,
  ScreenLayout,
  Team,
  TopBar,
  TrashIcon,
  useAuth
} from 'prove-common'
import { AppLogo } from '../components/app-logo/AppLogo'
import { SideBarLayout } from '../components/side-bar-layout/SideBarLayout'
import {
  buildAvdelingGroups,
  checkTeamResponse,
  filterTeams,
  sortTeams
} from './teams/teamsUtils'
import { TeamModal } from './teams/TeamModal'
import { TeamDeleteModal } from './teams/TeamDeleteModal'
import { useOrganizationData } from './teams/useOrganizationData'

type TeamTagProps = {
  team: Team
  onEdit: (team: Team) => void
  onDelete: (team: Team) => void
}

const TeamRow = ({ team, onEdit, onDelete }: TeamTagProps) => (
  <Mtds.Card data-pad='3'>
    <Mtds.Flex data-align='center' data-justify='space-between'>
      <strong data-self='200'>{team.name}</strong>
      <Mtds.Flex data-fixed data-gap='1'>
        <Mtds.Button
          data-variant='tertiary'
          data-size='sm'
          aria-label={`Rediger ${team.name}`}
          onClick={() => onEdit(team)}
        >
          <PencilSimpleIcon />
          Rediger
        </Mtds.Button>
        <Mtds.Button
          data-variant='tertiary'
          data-color='danger'
          data-size='sm'
          aria-label={`Slett ${team.name}`}
          onClick={() => onDelete(team)}
        >
          <TrashIcon />
          Slett
        </Mtds.Button>
      </Mtds.Flex>
    </Mtds.Flex>
  </Mtds.Card>
)

type TeamOwnerBlockProps = {
  title: string
  teams: Team[]
  onAddTeam: () => void
  onEditTeam: (team: Team) => void
  onDeleteTeam: (team: Team) => void
  disabled: boolean
}

const TeamOwnerBlock = ({
  title,
  teams,
  onAddTeam,
  onEditTeam,
  onDeleteTeam,
  disabled
}: TeamOwnerBlockProps) => (
  <Mtds.Group data-pad='4'>
    <Mtds.Grid data-gap='3'>
      <Mtds.Flex data-align='center' data-justify='space-between'>
        <Mtds.Prose>
          <Mtds.Heading as='h3' data-size='sm'>
            {title}
          </Mtds.Heading>
          <Mtds.Muted>
            {teams.length} {'team'}
          </Mtds.Muted>
        </Mtds.Prose>

        <Mtds.Button
          data-size='sm'
          disabled={disabled}
          onClick={onAddTeam}
          aria-label={`Legg til team i ${title}`}
        >
          <PlusIcon />
          Legg til team
        </Mtds.Button>
      </Mtds.Flex>

      <Mtds.Grid data-gap='2'>
        {teams.map((team) => (
          <TeamRow
            key={team.id}
            team={team}
            onEdit={onEditTeam}
            onDelete={onDeleteTeam}
          />
        ))}
      </Mtds.Grid>
    </Mtds.Grid>
  </Mtds.Group>
)

export const TeamsScreen = () => {
  const [isCreateModalOpen, setCreateModalOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<Team | undefined>()
  const [deleteTarget, setDeleteTarget] = useState<Team | undefined>()
  const [searchParams, setSearchParams] = useSearchParams()
  const searchText = searchParams.get('q') ?? ''
  const normalizedSearchText = searchText.trim().toLocaleLowerCase('nb')
  const setSearchText = (value: string) => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current)
        if (value) next.set('q', value)
        else next.delete('q')
        return next
      },
      { replace: true }
    )
  }
  const [createOwner, setCreateOwner] = useState<
    { avdelingId?: string; seksjonId?: string } | undefined
  >()
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  const {
    data: teams,
    isLoading,
    isError,
    refetch: refetchTeams
  } = useQuery({
    queryKey: ['teams'],
    staleTime: 1000 * 60 * 60 * 24,
    queryFn: async () => {
      const result = await authFetch().GET('/orgenhet-api/team')
      checkTeamResponse(result)
      if (!Array.isArray(result.data)) throw new Error('Ugyldig teamoversikt')
      return result.data.map((team) => ({ ...team, id: String(team.id) }))
    }
  })
  const {
    isRegionerError,
    isAvdelingerError,
    isSeksjonerError,
    isOrganizationError,
    isLoadingOrganization,
    organizationFetching,
    seksjonOptions,
    avdelingOptions,
    avdelingNameById,
    seksjonNameById,
    seksjonAvdelingById
  } = useOrganizationData()
  const teamsFetching = useIsFetching({ queryKey: ['teams'] }) > 0
  const organizationUnavailable =
    isOrganizationError || isLoadingOrganization || isError || teams == null
  if (
    searchParams.get('create') === 'true' &&
    !organizationUnavailable &&
    !isCreateModalOpen &&
    editTarget == null &&
    deleteTarget == null
  ) {
    setCreateOwner(undefined)
    setCreateModalOpen(true)
  }
  useEffect(() => {
    if (!isCreateModalOpen || searchParams.get('create') !== 'true') return
    const next = new URLSearchParams(searchParams)
    next.delete('create')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams, isCreateModalOpen])

  const sortedTeams = useMemo(
    () => sortTeams(teams ?? [], avdelingNameById, seksjonNameById),
    [avdelingNameById, seksjonNameById, teams]
  )
  const filteredTeams = useMemo(
    () =>
      filterTeams(
        sortedTeams,
        searchText,
        avdelingNameById,
        seksjonNameById,
        seksjonAvdelingById
      ),
    [
      avdelingNameById,
      searchText,
      seksjonAvdelingById,
      seksjonNameById,
      sortedTeams
    ]
  )
  const avdelingGroups = useMemo(
    () =>
      buildAvdelingGroups(
        filteredTeams,
        avdelingNameById,
        seksjonNameById,
        seksjonAvdelingById
      ),
    [avdelingNameById, filteredTeams, seksjonAvdelingById, seksjonNameById]
  )

  const isLoadingPage = isLoading || isLoadingOrganization

  return (
    <AppLayout
      sidebar={<SideBarLayout />}
      useMobileBar={false}
      isFullWidth
      topBar={<TopBar logo={<AppLogo />} title='Team' />}
    >
      <ScreenLayout
        header={
          <ScreenHeader
            title='Team'
            actions={
              <Mtds.Button
                data-variant='primary'
                disabled={organizationUnavailable}
                onClick={() => {
                  setCreateOwner(undefined)
                  setCreateModalOpen(true)
                }}
              >
                <PlusIcon />
                Legg til team
              </Mtds.Button>
            }
          />
        }
      >
        <Mtds.Group as={Mtds.Grid} data-gap='4' data-pad='4'>
          <p>
            Administrer team under avdelinger og seksjoner. Legg til ett eller
            flere team, endre navn og tilhørighet, eller slett team som ikke er
            i bruk.
          </p>
          <Mtds.Field
            as='input'
            type='search'
            label='Søk etter avdeling, seksjon eller team'
            placeholder='Søk etter navn'
            value={searchText}
            onChange={(event) => setSearchText(event.currentTarget.value)}
          />
          {teams != null && (
            <Mtds.Muted role='status'>
              {filteredTeams.length} av {teams.length} team
              {normalizedSearchText ? ' samsvarer med søket' : ''}
            </Mtds.Muted>
          )}
          {searchText && (
            <Mtds.Button
              data-variant='secondary'
              data-justify-self='start'
              onClick={() => setSearchText('')}
            >
              Nullstill søk
            </Mtds.Button>
          )}
        </Mtds.Group>
        {isError && (
          <Mtds.Alert data-color='danger' role='alert'>
            <Mtds.Heading as='h2' data-size='sm'>
              Kunne ikke hente team
            </Mtds.Heading>
            <p>
              {teams == null
                ? 'Teamoversikten er utilgjengelig.'
                : 'Viser sist hentede team. Oversikten kan være utdatert.'}{' '}
              Prøv igjen før du gjør endringer.
            </p>
            <Mtds.Button
              data-variant='secondary'
              disabled={teamsFetching}
              aria-busy={teamsFetching}
              onClick={() => void refetchTeams()}
            >
              Hent team på nytt
            </Mtds.Button>
          </Mtds.Alert>
        )}
        {isOrganizationError && (
          <Mtds.Alert data-color='danger' role='alert'>
            <Mtds.Heading as='h2' data-size='sm'>
              Kunne ikke hente organisasjonen
            </Mtds.Heading>
            <p>
              Kunne ikke hente{' '}
              {[
                isRegionerError && 'regioner',
                isAvdelingerError && 'avdelinger',
                isSeksjonerError && 'seksjoner'
              ]
                .filter(Boolean)
                .join(', ')}
              . Navn og tilhørighet kan være ufullstendige. Sist hentede data
              beholdes. Hent organisasjonen på nytt før du lagrer team.
            </p>
            <Mtds.Button
              data-variant='secondary'
              disabled={organizationFetching}
              aria-busy={organizationFetching}
              onClick={() =>
                void queryClient.refetchQueries({
                  type: 'active',
                  predicate: (query) =>
                    ['regioner', 'avdelinger', 'seksjoner'].includes(
                      String(query.queryKey[0])
                    )
                })
              }
            >
              Hent organisasjonen på nytt
            </Mtds.Button>
          </Mtds.Alert>
        )}
        {(teamsFetching || organizationFetching || isLoadingPage) && (
          <Mtds.Muted role='status'>
            {teams == null
              ? 'Henter team og organisasjon…'
              : 'Oppdaterer oversikten…'}
          </Mtds.Muted>
        )}
        {avdelingGroups.length ? (
          <Mtds.Grid data-gap='6'>
            {avdelingGroups.map((group) => {
              const teamCount = group.sections.reduce(
                (count, section) => count + section.teams.length,
                group.directTeams.length
              )

              return (
                <Mtds.Grid
                  as='section'
                  key={group.id}
                  data-gap='5'
                  aria-label={group.name}
                >
                  <Mtds.Flex data-align='center' data-justify='space-between'>
                    <Mtds.Prose>
                      <Mtds.Heading as='h2' data-size='md'>
                        {group.name}
                      </Mtds.Heading>
                      <Mtds.Muted>
                        {group.sections.length}{' '}
                        {group.sections.length === 1 ? 'seksjon' : 'seksjoner'}
                        {' / '}
                        {teamCount} team
                      </Mtds.Muted>
                    </Mtds.Prose>
                  </Mtds.Flex>
                  <Mtds.Grid data-gap='3'>
                    {group.directTeams.length > 0 && (
                      <TeamOwnerBlock
                        title='Avdeling'
                        disabled={organizationUnavailable}
                        teams={group.directTeams}
                        onAddTeam={() => {
                          setCreateOwner({ avdelingId: group.id })
                          setCreateModalOpen(true)
                        }}
                        onEditTeam={(team) => setEditTarget(team)}
                        onDeleteTeam={(team) => setDeleteTarget(team)}
                      />
                    )}
                    {group.sections.map((section) => (
                      <TeamOwnerBlock
                        key={section.id}
                        title={section.name}
                        disabled={organizationUnavailable}
                        teams={section.teams}
                        onAddTeam={() => {
                          setCreateOwner({
                            avdelingId: group.id,
                            seksjonId: section.id
                          })
                          setCreateModalOpen(true)
                        }}
                        onEditTeam={(team) => setEditTarget(team)}
                        onDeleteTeam={(team) => setDeleteTarget(team)}
                      />
                    ))}
                  </Mtds.Grid>
                </Mtds.Grid>
              )
            })}
          </Mtds.Grid>
        ) : !isLoadingPage && !isError && !isOrganizationError ? (
          <EmptyState
            title={normalizedSearchText ? 'Ingen treff' : 'Ingen team'}
            description={
              normalizedSearchText
                ? 'Prøv et annet søk på avdeling, seksjon eller team.'
                : 'Legg til et team for å komme i gang.'
            }
          />
        ) : null}
      </ScreenLayout>
      {/* Consume the quick-action URL before mounting the dialog's navigation blocker. */}
      {isCreateModalOpen && searchParams.get('create') !== 'true' && (
        <TeamModal
          isOpen={isCreateModalOpen}
          mode='create'
          organizationUnavailable={organizationUnavailable}
          existingTeams={teams ?? []}
          initialOwner={createOwner}
          avdelingOptions={avdelingOptions}
          seksjonOptions={seksjonOptions}
          onClose={() => {
            setCreateModalOpen(false)
            setCreateOwner(undefined)
          }}
        />
      )}
      {editTarget != null && (
        <TeamModal
          isOpen={editTarget != null}
          mode='edit'
          organizationUnavailable={organizationUnavailable}
          initialTeam={editTarget}
          existingTeams={teams ?? []}
          avdelingOptions={avdelingOptions}
          seksjonOptions={seksjonOptions}
          onClose={() => setEditTarget(undefined)}
        />
      )}
      {deleteTarget != null && (
        <TeamDeleteModal
          team={deleteTarget}
          onClose={() => setDeleteTarget(undefined)}
        />
      )}
    </AppLayout>
  )
}
